import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Pill, EmptyState } from "@/components/ui-bits";
import { Plus, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", company: "", contact_name: "", email: "", phone: "", gstin: "",
  billing_address: "", shipping_address: "", payment_terms: "", notes: "", brand_id: "",
};

export default function Clients() {
  const { brands, brandQuery, activeBrandId } = useBrand();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const r = await api.get("/clients", { params: brandQuery });
    setItems(r.data);
  }, [brandQuery]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, brand_id: activeBrandId !== "all" ? activeBrandId : brands[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, email: c.email || "" });
    setOpen(true);
  };
  const submit = async () => {
    try {
      const payload = { ...form, email: form.email || null };
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.company || c.name}?`)) return;
    await api.delete(`/clients/${c.id}`); load();
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="Directory"
        title="Clients"
        description="Companies and contacts billed across all brands."
        actions={
          <Button data-testid="new-client-btn" onClick={openCreate} className="bg-bronze text-black hover:bg-[#d4af68]">
            <Plus className="w-4 h-4 mr-1.5" /> New client
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState title="No clients yet" description="Create one or convert a lead to populate this list." />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Company</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Contact</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">GSTIN</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Brand</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Terms</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => {
                const brand = brands.find((b) => b.id === c.brand_id);
                return (
                  <TableRow key={c.id} className="border-border hover:bg-[#f8f6f2]" data-testid={`client-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.company || c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.billing_address?.split("\n")[0]}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{c.contact_name || c.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                        {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                        {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.gstin || "—"}</TableCell>
                    <TableCell>{brand ? <Pill>{brand.code}</Pill> : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.payment_terms || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(c)} className="text-muted-foreground hover:text-bronze" data-testid={`edit-client-${c.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(c)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="bg-white" data-testid="client-name" /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})} className="bg-white" /></div>
            <div><Label>Contact name</Label><Input value={form.contact_name} onChange={(e)=>setForm({...form, contact_name:e.target.value})} className="bg-white" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e)=>setForm({...form, gstin:e.target.value})} className="bg-white font-mono" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="bg-white" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="bg-white" /></div>
            <div className="col-span-2"><Label>Billing address</Label><Textarea rows={2} value={form.billing_address} onChange={(e)=>setForm({...form, billing_address:e.target.value})} className="bg-white" /></div>
            <div className="col-span-2"><Label>Shipping address</Label><Textarea rows={2} value={form.shipping_address} onChange={(e)=>setForm({...form, shipping_address:e.target.value})} className="bg-white" /></div>
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Brand" /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Payment terms</Label><Input value={form.payment_terms} onChange={(e)=>setForm({...form, payment_terms:e.target.value})} placeholder="Net 15" className="bg-white" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-client-btn" onClick={submit} className="bg-bronze text-black hover:bg-[#d4af68]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
